// Lógica PURA del acceso por sala (sin DOM/React), testeable en Node. El flag de
// habilitación (isEnabled) se combina fuera; aquí solo la pertenencia a salas.

export const SALAS_CONTROLABLES = [
  "servicios", "elemental", "intermedia", "superior", "stem", "ia", "universidad",
  "videoteca", "eventos", "adultos", "alfabetizacion", "finanzas", "empleo",
  // "Catálogo por grado": controlable desde la m038 (2026-08-19). Antes era una
  // ruta utilitaria y se le mostraba a todo menor sin forma de apagarla.
  "refuerzo",
  // "Videoteca Deportes": sala propia desde la m050 (2026-09-30). Estándar: NULL la concede.
  "deportes",
] as const;

// Salas OPT-IN: NUNCA se conceden por `allowed_rooms = NULL` ("todas"). Requieren estar
// listadas explícitamente. Así, al añadir una sala nueva y encender su flag, los usuarios
// legacy con NULL no la reciben solos: hay que otorgarla a mano (p. ej. a adultos/superior).
export const SALAS_OPT_IN = ["empleo"] as const;

// Salas estándar (no opt-in): las que cubre NULL = "todas". Exportada para que el
// editor pueda MOSTRAR una cuenta legacy con sus salas marcadas: si se pintara
// vacía, pulsar "Guardar" sin tocar nada le quitaría todo el acceso.
export const SALAS_ESTANDAR = SALAS_CONTROLABLES.filter((s) => !esOptIn(s));

/*
 * Las salas del modo kiosco. FIJA, no configurable por sede.
 *
 * Sin `elemental`, `intermedia` ni `superior`: llevan progreso e histórico, y una
 * sesión anónima compartida no tiene a quién atribuírselos.
 *
 * CON `videoteca` desde el 2026-08-31. Salió del conjunto inicial con el
 * argumento de que incrusta YouTube en un servicio dirigido a menores, y ese
 * argumento no se sostuvo al comprobarlo: la sala ya embebe por
 * `youtube-nocookie` con `rel=0` (`videoteca.ts`), que era justo la mitigación
 * que la spec ponía como condición para dejarla entrar. Y mientras tanto
 * `stem` —que sí estaba dentro— lanza a Khan Academy, PhET y GeoGebra en
 * pestaña nueva, sin ninguna. La línea dejaba fuera la sala protegida y dentro
 * las que no lo estaban.
 *
 * Lo que sí queda en pie es un riesgo de OPERACIÓN, no de privacidad: un vídeo
 * se reproduce solo en una pantalla pública que nadie supervisa. Es decisión de
 * producto tomada a sabiendas, no un descuido.
 *
 * La Videoteca no llama a la API —su catálogo es estático—, así que no necesita
 * entrada en `RUTAS_DEL_KIOSCO`. Si algún día se le añade un endpoint, hay que
 * abrirlo allí o fallará con 403.
 *
 * `deportes` (Videoteca Deportes, 2026-09-30) entra por el mismo argumento que
 * `videoteca`: mismo reproductor, mismo `embedUrl`, catálogo estático sin API.
 *
 * `empleo` es opt-in y va listada explícita: NULL nunca la concede.
 *
 * AÑADIR AQUÍ UNA SALA QUE ESCRIBA DATOS DE LA PERSONA cambia la naturaleza legal
 * de este modo, no solo su alcance.
 *
 * DESAJUSTE DELIBERADO con el backend: `backend/lambda/api/auth.ts` concede al
 * kiosco `GET /events` y `GET /events/rsvps` (RUTAS_DEL_KIOSCO), pero `eventos`
 * NO está aquí, así que el frontend nunca las llama. Es decisión de producto,
 * no un olvido — quitarlas del backend dejaría muertas las ramas `kiosk` que se
 * añadieron a propósito ahí (y que ya tienen sus pruebas). Si algún día
 * `eventos` entra al kiosco, esas rutas ya están abiertas del lado del
 * backend; si nunca entra, este comentario es la explicación de por qué esas
 * dos rutas siguen concedidas sin que nada las use.
 */
export const SALAS_KIOSCO = ["servicios", "stem", "universidad", "videoteca", "deportes", "empleo"] as const;

// Orden de preferencia para la redirección (salas de aprendizaje primero).
const ORDEN_DESTINO = [
  "servicios", "elemental", "intermedia", "superior", "stem", "universidad",
  "finanzas", "refuerzo", "videoteca", "deportes", "eventos", "adultos", "ia", "alfabetizacion", "empleo",
];

function esControlable(key: string): boolean {
  return (SALAS_CONTROLABLES as readonly string[]).includes(key);
}
function esOptIn(key: string): boolean {
  return (SALAS_OPT_IN as readonly string[]).includes(key);
}

/** ¿La sala `key` es visible para un usuario con estas `allowedRooms`? (ignora flags).
 *  Opt-in: solo si está listada explícitamente (NULL no la concede). Estándar: NULL = todas. */
export function salaPermitida(key: string, allowedRooms: string[] | null): boolean {
  // Utilitarias siempre visibles. OJO: por eso una clave INVENTADA también pasa;
  // quien reciba la sala de la URL (/:room/:slug) tiene que validar antes el par
  // sala–módulo contra ROOMS (ver `moduloEnSala` y SubjectDetailGuard).
  if (!esControlable(key)) return true;
  if (esOptIn(key)) return allowedRooms !== null && allowedRooms.includes(key);
  if (allowedRooms === null) return true; // null = todas las estándar
  return allowedRooms.includes(key);
}

/**
 * Primera sala permitida Y habilitada (por flag) a la que redirigir; si ninguna, "/perfil".
 *
 * HOY es inalcanzable para el kiosco: sus salas (SALAS_KIOSCO) están
 * siempre en ORDEN_DESTINO y `servicios` es la primera, así que este `for`
 * nunca las agota para él. Pero está a un flag de distancia de dejar de serlo
 * —si se apagaran a la vez los flags de todas sus salas— y ese día
 * esto devolvería "/perfil", que la guarda de App.tsx (session.role === "kiosk")
 * rebota de vuelta a home(session) === "/servicios". Si "/servicios" tampoco
 * estuviera habilitada, sería un ping-pong entre las dos rutas, no un valor
 * fijo. No se arregla aquí porque hoy no ocurre; que quede escrito para quien
 * toque los flags del kiosco.
 */
export function primeraSalaDestino(allowedRooms: string[] | null, habilitadas: string[]): string {
  for (const sala of ORDEN_DESTINO) {
    if (habilitadas.includes(sala) && salaPermitida(sala, allowedRooms)) return `/${sala}`;
  }
  return "/perfil";
}

/** Canonicaliza una lista de salas al valor explícito que se guardará. */
function canonicalizar(rooms: string[]): string[] {
  // `null` solo se lee para cuentas legacy. Guardar listas explícitas evita que
  // una sala añadida en el futuro aparezca sola a usuarios existentes.
  return [...new Set(rooms)].filter(esControlable);
}

/** Otorga `sala` a un usuario. Si tenía NULL ("todas") y se le concede una sala opt-in,
 *  se MATERIALIZA su lista (todas las estándar + la opt-in) para no quitarle lo demás. */
export function otorgarSala(allowedRooms: string[] | null, sala: string): string[] {
  const base = allowedRooms === null ? [...SALAS_ESTANDAR] : [...allowedRooms];
  if (!base.includes(sala)) base.push(sala);
  return canonicalizar(base);
}

/** Quita `sala`. Si tenía NULL y se le quita una sala estándar, se materializa (todas menos esa).
 *  Quitar una opt-in a un usuario NULL no cambia nada (NULL nunca la concedía). */
export function quitarSala(allowedRooms: string[] | null, sala: string): string[] {
  const base = (allowedRooms === null ? [...SALAS_ESTANDAR] : [...allowedRooms]).filter((s) => s !== sala);
  return canonicalizar(base);
}

/**
 * El grado cuyo CONTENIDO le toca ver a alguien.
 *
 * Kínder no tiene contenido propio: `elemental` va de 1ro a 6to y no hay una sola
 * lección etiquetada "Kínder". Sin esto, un niño de Kínder abre la sala y ve una
 * rejilla vacía sin entender por qué.
 *
 * Se mapea a 1ro en vez de duplicar 190 lecciones: el contenido es el mismo y
 * duplicarlo significaría mantener dos copias que se separan con el tiempo.
 * Decisión de Alida (2026-08-19): "por ahora Kínder ve 1ero".
 *
 * El día que exista contenido propio de Kínder, se borra este caso y ya está.
 */
export function gradoDeContenido(grade: string | null): string | null {
  if (grade === "Kínder") return "1ro";
  return grade;
}

/**
 * Nivel de juegos que le corresponde a un grado.
 *
 * PRESELECCIONA el filtro de la sala de juegos; NO restringe. El niño puede
 * cambiarlo, igual que puede repasar el contenido de otro grado — decisión de
 * producto del 2026-08-18. La sala abre en lo suyo sin cerrarle el resto.
 *
 * Vive aquí y no en accessProfiles.ts a propósito: ese módulo importa `apiFetch`,
 * que arrastra código de navegador, y esta lógica se prueba en Node.
 */
export function nivelJuegosParaGrado(grade: string | null): "todos" | "elemental" | "mayores" {
  if (!grade || grade === "adulto") return "todos";
  if (grade === "Kínder") return "elemental";
  const numero = Number.parseInt(grade, 10);
  if (!Number.isFinite(numero)) return "todos";
  return numero <= 6 ? "elemental" : "mayores";
}
